// imageUpload.js
// Utility functions for uploading images to Firebase Storage

import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebaseConfig';

/**
 * Upload an image file to Firebase Storage
 * @param {File} file - The image file to upload
 * @param {string} folder - The folder path in storage (e.g., 'events', 'profiles')
 * @returns {Promise<string>} - The download URL of the uploaded image
 */
export const uploadImage = async (file, folder = 'events') => {
  try {
    if (!file) {
      throw new Error('No file provided');
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a valid image (JPEG, PNG, GIF, or WebP)');
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('File size exceeds 5MB limit');
    }

    // Generate unique filename
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const fileExtension = file.name.split('.').pop();
    const fileName = `${timestamp}_${randomString}.${fileExtension}`;
    
    // Create storage reference
    const storageRef = ref(storage, `${folder}/${fileName}`);
    
    // Upload file
    const snapshot = await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

/**
 * Delete an image from Firebase Storage
 * @param {string} imageUrl - The download URL of the image to delete
 */
export const deleteImage = async (imageUrl) => {
  try {
    if (!imageUrl) {
      return;
    }

    // Extract the path from the URL
    const decodedUrl = decodeURIComponent(imageUrl);
    const pathMatch = decodedUrl.match(/\/o\/(.+?)\?/);
    
    if (pathMatch && pathMatch[1]) {
      const imagePath = pathMatch[1];
      const imageRef = ref(storage, imagePath);
      await deleteObject(imageRef);
    }
  } catch (error) {
    console.error('Error deleting image:', error);
    // Don't throw error, just log it as the image might already be deleted
  }
};

/**
 * Update an image - delete old and upload new
 * @param {File} newFile - The new image file to upload
 * @param {string} oldImageUrl - The URL of the old image to delete
 * @param {string} folder - The folder path in storage
 * @returns {Promise<string>} - The download URL of the new image
 */
export const updateImage = async (newFile, oldImageUrl, folder = 'events') => {
  try {
    // Upload new image
    const newImageUrl = await uploadImage(newFile, folder);
    
    // Delete old image if it exists
    if (oldImageUrl) {
      await deleteImage(oldImageUrl);
    }
    
    return newImageUrl;
  } catch (error) {
    console.error('Error updating image:', error);
    throw error;
  }
};
